import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NiIconComponent } from '../ni-icon/ni-icon.component';

export interface NiOption {
  value: any;
  label: string;
  code?: string;
  disabled?: boolean;
  group?: string;
  icon?: string;
  trailingIcon?: string;
  profileImage?: string;
  trailingProfileImage?: string;
  imageLoaded?: boolean;
  trailingImageLoaded?: boolean;
  isFavorite?: boolean;
  badgeCount?: number;
  [key: string]: any;
}

@Component({
  selector: 'ni-option',
  templateUrl: './ni-option.component.html',
  styleUrls: ['./ni-option.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, NiIconComponent]
})
export class NiOptionComponent {
  constructor(private readonly cdr: ChangeDetectorRef) {}
  @Input() option!: NiOption;
  @Input() multiple: boolean = false;
  @Input() selected: boolean = false;
  @Input() highlighted: boolean = false;
  @Input() optionLabel: string | number = 'label';
  @Input() checkIcon: string = 'check';
  @Input() showFavoriteToggle: boolean = true;
  @Input() showBadge: boolean = true;
  @Input() showRadioBox: boolean = false;

  @Output() optionClick = new EventEmitter<NiOption>();
  @Output() imageError = new EventEmitter<{event: Event, option: NiOption, type: 'profileImage' | 'trailingProfileImage'}>();
  @Output() favoriteToggle = new EventEmitter<{option: NiOption, isFavorite: boolean}>();

  getOptionLabel(option: NiOption): string {
    return option[this.optionLabel] || option['label'] || option['name'] || option['value'] || '';
  }

  getFirstLetter(option: NiOption): string {
    const label = this.getOptionLabel(option);
    return label.charAt(0).toUpperCase();
  }

  isOptionSelected(option: NiOption): boolean {
    return this.selected;
  }

  getAriaSelected(): string {
    return this.isOptionSelected(this.option) ? 'true' : 'false';
  }

  onImageError(event: Event, option: NiOption, type: 'profileImage' | 'trailingProfileImage'): void {
    // Mark the image as not loaded based on type
    if (type === 'profileImage') {
      option['imageLoaded'] = false;
    } else if (type === 'trailingProfileImage') {
      option['trailingImageLoaded'] = false;
    }
    
    // Emit the event to parent component
    this.imageError.emit({ event, option, type });
    
    // Trigger change detection to update the UI
    this.cdr.detectChanges();
  }

  onOptionClick(): void {
    this.optionClick.emit(this.option);
  }

  onFavoriteToggle(event: Event): void {
    event.stopPropagation();
    const newFavoriteState = !this.option.isFavorite;
    this.favoriteToggle.emit({ option: this.option, isFavorite: newFavoriteState });
  }

  onOptionKeyDown(event: KeyboardEvent): void {
    // Handle Enter and Space keys for keyboard navigation
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onOptionClick();
    }
  }
} 